import type { NormalizedError } from '../api/errors';
import type {
  AccountMe,
  MarketPurchaseIntent,
  AbuseSignalRecord,
  AdminOpsSummary,
  AdminConfigRecord,
  Cosmetic,
  CosmeticClaimCampaignStatus,
  CosmeticFounderClaimStatus,
  CosmeticListing,
  EquippedCosmetic,
  EligibilityRefresh,
  EligibilityStatus,
  EngineEvent,
  EngineOutcome,
  EquipmentResponse,
  GoldSettlement,
  HubEnterResponse,
  HubFacing,
  HubInspectResponse,
  InventoryItem,
  LeftBehindItem,
  GoldSaleOrder,
  GoldSalePurchaseIntent,
  MarketListing,
  MerchantStatus,
  PassStatus,
  Rarity,
  RunState,
  RunReplayResult,
  SealedRunItem,
  SpinnerStatus,
  SpinReward,
  SpectateRunSummary,
  SpectateFrame,
  SpectateWaystationPreview,
} from '../api/types';
import type { WalletKind } from '../auth/wallet';

export type View =
  | 'landing'
  | 'how-to-play'
  | 'docs'
  | 'roadmap'
  | 'spectate'
  | 'waystation'
  | 'dashboard'
  | 'run'
  | 'inventory'
  | 'merchant'
  | 'marketplace'
  | 'cashout'
  | 'spinner'
  | 'cosmetics'
  | 'admin';

/** An open spectate replay: its summary, the full frame list, the cursor, and play state. */
export interface SpectateSession {
  summary: SpectateRunSummary;
  frames: SpectateFrame[];
  index: number;
  playing: boolean;
}

/** Gold Exchange order sorting: best Gold/$OBOL rate first is the default. */
export type CashoutSort = 'rate' | 'gold' | 'newest';

/** Marketplace browse category: gear slots plus player-to-player cosmetics. */
export type MarketCategory = 'weapon' | 'armor' | 'trinket' | 'lantern' | 'cosmetics';
export type MarketRarityFilter = Rarity | 'all';
export type MarketSort = 'newest' | 'price';

/**
 * Connected wallet's $OBOL balance snapshot for the paid-spinner preflight.
 * `amountRaw` is smallest units (string — may exceed Number range). `error`
 * marks a failed fetch: the paid "Pay with wallet" button fails closed on it.
 */
export interface SpinnerWalletBalance {
  mint: string;
  amountRaw: string;
  decimals: number | null;
}

/**
 * A wallet payment already sent from this device for a marketplace $OBOL
 * purchase intent, keyed by intent id in {@link AppState.marketIntentPayments}.
 * While an entry exists the UI must NEVER reopen Phantom for that intent —
 * settlement retries reuse the stored signature instead (no double payment).
 * 'confirming' = an automatic retry loop is running right now;
 * 'unconfirmed' = retries stopped without settlement — offer "Retry settlement".
 */
export interface MarketIntentPayment {
  signature: string;
  status: 'confirming' | 'unconfirmed';
  listingKind: 'gear' | 'cosmetic';
}

/**
 * A wallet payment already sent from this device for a Gold Exchange purchase
 * intent, keyed by intent id in {@link AppState.cashoutIntentPayments}.
 * Same contract as {@link MarketIntentPayment}: while an entry exists the UI
 * must NEVER reopen Phantom for that intent — retries reuse the signature.
 */
export interface CashoutIntentPayment {
  signature: string;
  status: 'confirming' | 'unconfirmed';
}

export type TerminalResult =
  | { kind: 'extracted'; settlement: GoldSettlement; sealed: SealedRunItem[]; leftBehind: LeftBehindItem[]; practiceOnly?: boolean; message?: string }
  | { kind: 'died'; cause?: string; floor: number; carriedGoldLost: number; practiceOnly?: boolean }
  | { kind: 'abandoned'; floor: number; carriedGoldLost: number; practiceOnly?: boolean };

export interface PendingBindEquip {
  itemInstanceId: string;
  slot: import('../api/types').EquipSlot;
  title: string;
}

export interface AppState {
  apiBase: string;
  /** Whether the dev-only affordances (throwaway keypair) are exposed. */
  devMode: boolean;
  view: View;

  // auth
  walletKind: WalletKind | null;
  walletAddress: string | null;
  session: string | null;
  account: AccountMe | null;

  // status
  eligibility: (EligibilityStatus & Partial<EligibilityRefresh>) | null;
  pass: PassStatus | null;

  // run
  run: RunState | null;
  runId: string | null;
  lastOutcome: EngineOutcome | null;
  lastEvents: EngineEvent[];
  terminal: TerminalResult | null;

  // inventory
  inventory: InventoryItem[] | null;
  equipment: EquipmentResponse | null;
  selectedInventoryItemId: string | null;
  pendingBindEquip: PendingBindEquip | null;
  /** Set when the Inventory cosmetics collection failed to load (items still shown). */
  inventoryCosmeticsError: string | null;

  // waystation hub
  hub: HubEnterResponse | null;
  hubInspect: HubInspectResponse | null;
  hubPosition: { x: number; y: number } | null;
  hubFacing: HubFacing;
  hubNearbyStationId: string | null;

  // marketplace (Phase G)
  marketListings: MarketListing[] | null;
  myListings: MarketListing[] | null;
  goldBalance: number | null;
  marketCategory: MarketCategory;
  /** Which listing currencies are visible (both on by default). */
  marketCurrencies: { gold: boolean; obol: boolean };
  /** Buyer's active $OBOL purchase intents (pending wallet payments). */
  myMarketIntents: MarketPurchaseIntent[] | null;
  /** Wallet payments already sent for purchase intents, keyed by intent id. */
  marketIntentPayments: Record<string, MarketIntentPayment>;
  marketWalletBalance: { mint: string; amountRaw: string; decimals: number | null } | null;
  marketWalletBalanceError: boolean;
  marketRarity: MarketRarityFilter;
  marketSort: MarketSort;
  /** Cosmetic skin filter for the Marketplace Cosmetics category ('all' or a cosmetic id). */
  marketSkin: string;

  // Merchant supplies and repair
  merchant: MerchantStatus | null;
  rewardLoadoutExtras: { oil: number; torch: number; potion: number };

  // Gold cash-out / Gold Exchange (Phase H)
  cashoutOrders: GoldSaleOrder[] | null;
  myCashoutOrders: GoldSaleOrder[] | null;
  cashoutGoldBalance: number | null;
  /** $OBOL mint decimals for player-facing rate display (best-effort). */
  cashoutTokenDecimals: number | null;
  cashoutSort: CashoutSort;
  /** Buyer's active Gold Exchange purchase intents (pending wallet payments). */
  myCashoutIntents: GoldSalePurchaseIntent[] | null;
  /** Wallet payments already sent for Gold Exchange intents, keyed by intent id. */
  cashoutIntentPayments: Record<string, CashoutIntentPayment>;

  // spinner / Ferryman's Wager (Phase I)
  spinner: SpinnerStatus | null;
  lastSpinReward: SpinReward | null;
  /** Wallet $OBOL balance for the paid-spin preflight (null until fetched). */
  spinnerWalletBalance: SpinnerWalletBalance | null;
  /** True when the last balance fetch failed — paid payment fails closed. */
  spinnerWalletBalanceError: boolean;

  // cosmetics (Phase K)
  cosmeticsShop: Cosmetic[] | null;
  myCosmetics: Cosmetic[] | null;
  equippedCosmetics: EquippedCosmetic[] | null;
  cosmeticListings: CosmeticListing[] | null;
  myCosmeticListings: CosmeticListing[] | null;
  cosmeticClaimCampaigns: CosmeticClaimCampaignStatus[] | null;
  cosmeticFounderClaim: CosmeticFounderClaimStatus | null;
  cosmeticsGoldBalance: number | null;

  // admin config (Phase L1)
  adminToken: string | null;
  adminConfig: AdminConfigRecord[] | null;
  adminAbuseSignals: AbuseSignalRecord[] | null;
  adminSelectedAbuseSignal: AbuseSignalRecord | null;
  adminReplayResult: RunReplayResult | null;
  adminOpsSummary: AdminOpsSummary | null;

  // spectate / non-holder funnel (Phase J)
  spectateRuns: SpectateRunSummary[] | null;
  spectateSession: SpectateSession | null;
  spectatePreview: SpectateWaystationPreview | null;

  // ui
  busy: string | null;
  error: NormalizedError | null;
  notice: string | null;
}

export function initialState(apiBase: string, devMode: boolean): AppState {
  return {
    apiBase,
    devMode,
    view: 'landing',
    walletKind: null,
    walletAddress: null,
    session: null,
    account: null,
    eligibility: null,
    pass: null,
    run: null,
    runId: null,
    lastOutcome: null,
    lastEvents: [],
    terminal: null,
    inventory: null,
    equipment: null,
    selectedInventoryItemId: null,
    pendingBindEquip: null,
    inventoryCosmeticsError: null,
    hub: null,
    hubInspect: null,
    hubPosition: null,
    hubFacing: 'south',
    hubNearbyStationId: null,
    marketListings: null,
    myListings: null,
    goldBalance: null,
    marketCategory: 'weapon',
    marketCurrencies: { gold: true, obol: true },
    myMarketIntents: null,
    marketIntentPayments: {},
    marketWalletBalance: null,
    marketWalletBalanceError: false,
    marketRarity: 'all',
    marketSort: 'newest',
    marketSkin: 'all',
    merchant: null,
    rewardLoadoutExtras: { oil: 0, torch: 0, potion: 0 },
    cashoutOrders: null,
    myCashoutOrders: null,
    cashoutGoldBalance: null,
    cashoutTokenDecimals: null,
    cashoutSort: 'rate',
    myCashoutIntents: null,
    cashoutIntentPayments: {},
    spinner: null,
    lastSpinReward: null,
    spinnerWalletBalance: null,
    spinnerWalletBalanceError: false,
    cosmeticsShop: null,
    myCosmetics: null,
    equippedCosmetics: null,
    cosmeticListings: null,
    myCosmeticListings: null,
    cosmeticClaimCampaigns: null,
    cosmeticFounderClaim: null,
    cosmeticsGoldBalance: null,
    adminToken: null,
    adminConfig: null,
    adminAbuseSignals: null,
    adminSelectedAbuseSignal: null,
    adminReplayResult: null,
    adminOpsSummary: null,
    spectateRuns: null,
    spectateSession: null,
    spectatePreview: null,
    busy: null,
    error: null,
    notice: null,
  };
}

/** Tiny observable store: immutable patch + subscriber notification. */
export class Store {
  private state: AppState;
  private readonly listeners = new Set<(s: AppState) => void>();

  constructor(initial: AppState) {
    this.state = initial;
  }

  get(): AppState {
    return this.state;
  }

  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  subscribe(fn: (s: AppState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}
